/**
 * useBuildVersion — detects when a newer frontend build has been deployed.
 *
 * Captures the bundle hash this session is running on first load, then polls
 * /asset-manifest.json (cache-busted) periodically + on window focus. When the
 * server advertises a different hash, `updateAvailable` flips true. `reload()`
 * clears any caches and reloads to pull the latest bundle. Reload is ALWAYS
 * user-initiated — we never auto-reload, so an active ride/trip is never
 * interrupted.
 */
import { useEffect, useRef, useState } from "react";
import { BUILD_RELEASE, fetchDeployedBundle } from "../buildInfo";

export function useBuildVersion({ pollMs = 60000 } = {}) {
  const [loadedHash, setLoadedHash] = useState(null);
  const [latestHash, setLatestHash] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const loadedRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      const info = await fetchDeployedBundle();
      if (!alive || !info) return;
      if (!loadedRef.current) {
        loadedRef.current = info.hash;
        setLoadedHash(info.hash);
      }
      setLatestHash(info.hash);
      if (loadedRef.current && info.hash && info.hash !== loadedRef.current) {
        setUpdateAvailable(true);
      }
    };
    check();
    const timer = setInterval(check, pollMs);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [pollMs]);

  const reload = async () => {
    try {
      if (window.caches && caches.keys) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch {
      /* best-effort */
    }
    window.location.reload();
  };

  return { release: BUILD_RELEASE, loadedHash, latestHash, updateAvailable, reload };
}
